import React, { useState, useEffect } from "react";
import Header from "../common/layout/header";
import Footer from "../common/layout/footer";
import { http } from "../helper";
import ShowAlert from "../common/Showalert";
import { BaseUrl } from "../common/config";
import CommonAlert from "../common/Commonalert";
import Sidebar from "../common/sidebar";
import { menuPermissions } from "../account/menupermissions";
import { useTranslation } from "react-i18next";

const AddRoles = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [datalist, setDataList] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]); // module-level strings e.g. manage_products
  const [selectedSubPermissions, setSelectedSubPermissions] = useState({}); // { manage_products: ['manage_products.view', ...] }
  const [selectedReports, setSelectedReports] = useState({});
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState([]);
  const [alertPopup, setAlertPopup] = useState(false);
  const [alerterrorPopup, setAlertErrorPopup] = useState(false);
  const [alerterrMessage, setAlerterrMessage] = useState([]);

  if (!sessionStorage.getItem("isLoggedIn")) {
    window.location.href = "/";
  }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      var loginDetails = {};
      if (sessionStorage.getItem("authData")) {
        loginDetails = JSON.parse(sessionStorage.getItem("authData"));
      }
      let URL = BaseUrl + `admin/getRoles`;
      const response = await http.get(URL);
      let responseData = response.data.data;
      if (response.data.success === true) {
        setDataList(responseData || []);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };
  // helpers
  const getDefaultSubPerms = (moduleValue) => {
    const mod = menuPermissions.find((m) => m.value === moduleValue);
    if (!mod || !mod.subPermissions) return [];
    return mod.subPermissions.filter((s) => s.default).map((s) => s.value);
  };

  const toggleModule = (moduleValue, checked) => {
    const isDashboard = moduleValue === 'manage_dashboard';
    if (checked) {
      setSelectedPermissions((prev) =>
        Array.from(new Set([...prev, moduleValue])),
      );
      if (isDashboard) {
        // Select ALL report groups and enable view permission for each
        const dashMod = menuPermissions.find((m) => m.value === 'manage_dashboard');
        const allReports = dashMod?.reportGroups || [];
        const allReportSelected = {};
        const allViewPerms = [];
        allReports.forEach((r) => {
          allReportSelected[r.value] = true;
          const viewPerm = r.permissions.find((p) => p.value.endsWith('.view'));
          if (viewPerm) allViewPerms.push(viewPerm.value);
        });
        setSelectedReports(allReportSelected);
        setSelectedSubPermissions((prev) => ({
          ...prev,
          [moduleValue]: allViewPerms,
        }));
      } else {
        setSelectedSubPermissions((prev) => {
          const prevArr = prev[moduleValue];
          if (prevArr && prevArr.length) return { ...prev };
          return { ...prev, [moduleValue]: getDefaultSubPerms(moduleValue) };
        });
      }
      setErrors((prev) => ({ ...prev, permissions: "" }));
    } else {
      setSelectedPermissions((prev) => prev.filter((p) => p !== moduleValue));
      setSelectedSubPermissions((prev) => {
        const copy = { ...prev };
        delete copy[moduleValue];
        return copy;
      });
      if (isDashboard) {
        setSelectedReports({});
      }
    }
  };

  const toggleSubPermission = (moduleValue, subValue, checked) => {
    setSelectedSubPermissions((prev) => {
      const curr = new Set(prev[moduleValue] || []);
      if (checked) curr.add(subValue);
      else curr.delete(subValue);
      return { ...prev, [moduleValue]: Array.from(curr) };
    });
    setErrors((prev) => ({ ...prev, permissions: "" }));
  };
  // Dashboard: toggle an entire report group
  const toggleReport = (reportValue, checked, reportGroup) => {
    setSelectedReports((prev) => ({ ...prev, [reportValue]: checked }));
    if (checked) setErrors((prev) => ({ ...prev, dashboardReports: "" }));
    setSelectedSubPermissions((prev) => {
      const currentPerms = new Set(prev['manage_dashboard'] || []);
      if (checked) {
        // Always add view when enabling a report
        const viewPerm = reportGroup.permissions.find((p) => p.value.endsWith('.view'));
        if (viewPerm) currentPerms.add(viewPerm.value);
      } else {
        // Remove all perms belonging to this report group
        reportGroup.permissions.forEach((p) => currentPerms.delete(p.value));
      }
      return { ...prev, manage_dashboard: Array.from(currentPerms) };
    });
  };
  // Dashboard: toggle view/export within a report group
  const toggleReportPermission = (reportGroup, permValue, checked) => {
    const isViewPerm = permValue.endsWith('.view');
    const exportPerm = reportGroup.permissions.find((p) => p.value.endsWith('.export'))?.value;
    setSelectedSubPermissions((prev) => {
      const currentPerms = new Set(prev['manage_dashboard'] || []);
      if (checked) {
        currentPerms.add(permValue);
      } else {
        currentPerms.delete(permValue);
        // Disabling View must also disable Export
        if (isViewPerm && exportPerm) currentPerms.delete(exportPerm);
      }
      return { ...prev, manage_dashboard: Array.from(currentPerms) };
    });
    // If View is turned off, also uncheck the report checkbox itself
    if (isViewPerm && !checked) {
      setSelectedReports((prev) => ({ ...prev, [reportGroup.value]: false }));
    }
  };

  const formvalidation = () => {
    const newErrors = {};
    let isValid = true;
    if (roleName.trim() === "") {
      newErrors.roleName = t("field_required_txt");
      isValid = false;
    }
    if (selectedPermissions.length === 0) {
      newErrors.permissions = t("select_at_least_one_permission_txt");
      isValid = false;
    }
    // If Dashboard is selected, at least one report group must be checked
    if (selectedPermissions.includes('manage_dashboard')) {
      const anyReportSelected = Object.values(selectedReports).some(Boolean);
      if (!anyReportSelected) {
        newErrors.dashboardReports = t("select_at_least_one_report_txt");
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  const btnsubmit = async (e) => {
    e.preventDefault();
    if (!formvalidation()) return;
    // build permissions payload: module-level + selected sub permissions
    let permissions = [];
    selectedPermissions.forEach((mod) => {
      permissions.push(mod);
      const subs = selectedSubPermissions[mod] || [];
      permissions = permissions.concat(subs);
    });
    // deduplicate
    permissions = Array.from(new Set(permissions));
    const payload = {
      roleName: roleName,
      permissions,
    };
    // client side duplicate roleName check (case-insensitive)
    const isFound = datalist.some(
      (element) =>
        element.roleName &&
        element.roleName.toLowerCase() === roleName.toLowerCase(),
    );
    if (isFound) {
      setAlertErrorPopup(true);
      setAlerterrMessage(t("role_name_already_exists_txt"));
      return;
    }
    try {
      setIsLoading(true);
      const URL = BaseUrl + "admin/addRole/";
      const response = await http.post(URL, payload);
      if (response.data.success === true) {
        setAlertPopup(true);
        setAlertMessage(t("role_permissions_added_successfully_txt"));
        // Reset form
        setRoleName("");
        setSelectedPermissions([]);
        setSelectedSubPermissions({});
        setSelectedReports({});
        // refresh role list
        fetchData();
      } else {
        setAlertErrorPopup(true);
        setAlerterrMessage(response.data.message || t("failed_to_add_role_txt"));
      }
    } catch (err) {
      console.log(err);
      setAlertErrorPopup(true);
      setAlerterrMessage(t("server_error_adding_role_txt"));
    } finally {
      setIsLoading(false);
    }
  };

  const routeChange = () => {
    window.location.href = `/role/`;
  };

  return (
    <section className="dashboard-block">
      {alerterrorPopup && <ShowAlert title={t("alert_txt")} message={alerterrMessage} />}
      {alertPopup && <CommonAlert title={t("alert_txt")} message={alertMessage} />}
      <Sidebar />
      <div className="dash-left">
        <Header />
        <section className="cart-block">
          <div className="container account-details">
            <h1 className="checkout-title">{t("add_role_txt")}</h1>
            <div className="right-table-outer-wrap">
              <div className="form-field freeproduct-fieldset addroles-fieldset form-container-xs">
                <form className="client-detail-form" onSubmit={btnsubmit}>
                  <div className="input-field">
                    <label htmlFor="name">{t("role_name_txt")}</label>
                    <input
                      type="text"
                      name="roleName"
                      value={roleName}
                      onChange={(e) => {
                        setRoleName(e.target.value);
                        setErrors((prev) => ({ ...prev, roleName: "" }));
                      }}
                    />
                    {errors.roleName && (
                      <div className="error-msg">{errors.roleName}</div>
                    )}
                  </div>
                  <div className="input-field">
                    <label>{t("permissions_txt")}</label>
                    <div className="custom_check-box">
                      {menuPermissions.map((module, index) => {
                        const modVal = module.value;
                        const moduleChecked =
                          selectedPermissions.includes(modVal);
                        const subSelected =
                          selectedSubPermissions[modVal] || [];
                        const isDashboard   = modVal === 'manage_dashboard';
                        return (
                          <div className="check-box" key={modVal}>
                            {/* ── Module-level checkbox ── */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              <input
                                id={`chkbox${index}`}
                                type="checkbox"
                                value={modVal}
                                checked={moduleChecked}
                                onChange={(e) =>
                                  toggleModule(modVal, e.target.checked)
                                }
                              />
                              <label htmlFor={`chkbox${index}`}>
                                {t(module.name)}
                              </label>
                            </div>
                            {/* ── Dashboard: hierarchical report groups ── */}
                            {moduleChecked && isDashboard && module.reportGroups && (
                              <div className="rolls-action-bx" style={{ marginTop: "8px", paddingLeft: "24px" }}>
                                {module.reportGroups.map((reportGroup) => {
                                  const reportChecked = !!selectedReports[reportGroup.value];
                                  const viewPermVal   = reportGroup.permissions.find((p) => p.value.endsWith('.view'))?.value;
                                  const exportPermVal = reportGroup.permissions.find((p) => p.value.endsWith('.export'))?.value;
                                  const viewChecked   = subSelected.includes(viewPermVal);
                                  const exportChecked = subSelected.includes(exportPermVal);
                                  return (
                                    <div key={reportGroup.value} style={{ marginBottom: "10px" }}>
                                      {/* Report group checkbox */}
                                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <input
                                          id={`rpt-${reportGroup.value}`}
                                          type="checkbox"
                                          checked={reportChecked}
                                          onChange={(e) =>
                                            toggleReport(reportGroup.value, e.target.checked, reportGroup)
                                          }
                                        />
                                        <label htmlFor={`rpt-${reportGroup.value}`} style={{ fontWeight: 500 }}>
                                          {t(reportGroup.name)}
                                        </label>
                                      </div>
                                      {/* View / Export toggles — only when report is checked */}
                                      {reportChecked && (
                                        <div
                                          className="switch-group"
                                          style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "6px", paddingLeft: "28px" }}
                                        >
                                          {/* View toggle — always on when report is checked, disabled */}
                                          <div className="switch-item" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <span className="switch-label">{t("view_txt")}</span>
                                            <label className="switch switch-ios">
                                              <input
                                                type="checkbox"
                                                checked={viewChecked}
                                                disabled={false}
                                                onChange={(e) =>
                                                  toggleReportPermission(reportGroup, viewPermVal, e.target.checked)
                                                }
                                              />
                                              <span className="slider-ios"></span>
                                            </label>
                                          </div>
                                          {/* Export toggle — disabled when view is off */}
                                          <div className="switch-item" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            <span className="switch-label">{t("export_btn_txt")}</span>
                                            <label className="switch switch-ios">
                                              <input
                                                type="checkbox"
                                                checked={exportChecked}
                                                disabled={!viewChecked}
                                                onChange={(e) =>
                                                  toggleReportPermission(reportGroup, exportPermVal, e.target.checked)
                                                }
                                              />
                                              <span className="slider-ios"></span>
                                            </label>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {/* Dashboard report validation error */}
                            {moduleChecked && isDashboard && errors.dashboardReports && (
                              <div className="error-msg" style={{ paddingLeft: "24px", marginTop: "4px" }}>
                                {errors.dashboardReports}
                              </div>
                            )}
                            {/* All other modules: existing flat sub-permission toggles */}
                            {moduleChecked && !isDashboard && module.subPermissions && (
                              <div className="rolls-action-bx" style={{ marginTop: "8px" }}>
                                <div
                                  className="switch-group"
                                  style={{
                                    display: "flex",
                                    gap: "12px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {module.subPermissions.map((sub) => {
                                    const subChecked = subSelected.includes(
                                      sub.value,
                                    );
                                    const isViewToggle =
                                      sub.value.endsWith(".view") ||
                                      sub.value.endsWith(".refresh");
                                    return (
                                      <div
                                        className="switch-item"
                                        key={sub.value}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "6px",
                                        }}
                                      >
                                        <span className="switch-label">
                                          {t(sub.name)}
                                        </span>
                                        <label className="switch switch-ios">
                                          <input
                                            type="checkbox"
                                            checked={subChecked}
                                            disabled={isViewToggle}
                                            onChange={(e) => {
                                              if (!isViewToggle) {
                                                toggleSubPermission(
                                                  modVal,
                                                  sub.value,
                                                  e.target.checked,
                                                );
                                              }
                                            }}
                                          />
                                          <span className="slider-ios"></span>
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {errors.permissions && (
                      <div className="error-msg">{errors.permissions}</div>
                    )}
                  </div>
                  <div className="btnfield" style={{ marginTop: "16px" }}>
                    <button title={t("add_role_txt")} type="submit" name="btnsubmit" disabled={isLoading}>
                      {t("add_role_txt")}
                    </button>
                    &nbsp;&nbsp;&nbsp;
                    <button title={t("cancel_txt")} type="button" onClick={routeChange}>
                      {t("cancel_txt")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <Footer />
        </section>
      </div>
    </section>
  );
};

export default AddRoles;
